import { Link } from 'react-router-dom';
import { forwardRef } from 'react';

import useWhiteLabelComponent from '../../hooks/useWhiteLabelComponent';
import useAsyncButtonAction from '../../hooks/useAsyncButtonAction';

import { TEXT_GREY, VL_BLACK, WHITE } from '../../utils/colours';

import Spinner from '../spinner';
import { StyledButton, TextButton } from './Button.styles';

/** Button component which supports both blue and purple gradients for primary buttons. Offers loading and disabled states, as well as two size options. */
const Button = forwardRef((props, ref) => {
    if (props.asyncAction) {
        return <AsyncActionButton {...props} ref={ref} />;
    } else {
        return <ButtonComponent {...props} ref={ref} />;
    }
});

const ButtonComponent = forwardRef(
    (
        {
            children,
            variant = 'primary',
            square = false,
            loading = false,
            type = 'button',
            large = false,
            link,
            marginTop = false,
            stopPropagation = false,
            ...props
        },
        ref
    ) => {
        const { accentColour, accentColour2, useAccentColour2, whiteOverAccent, isVideoloft } = useWhiteLabelComponent();
        const alternativeTextColour = whiteOverAccent || (isVideoloft && accentColour2) ? WHITE() : VL_BLACK();

        // Don't put type='button' if not using button tag
        type = link || (props.as && props.as !== 'button') ? undefined : type;

        // Additional props if rendering as a link
        let linkProps = {};
        if (link) {
            // Use React Router's Link component if it is a relative link
            if (link.charAt(0) === '/') {
                linkProps.as = Link;
                linkProps.to = link;
            } else {
                linkProps.as = 'a';
                linkProps.href = link;
            }
        }

        if (variant === 'text' || variant === 'text-dark' || variant === 'inline' || variant === 'text-hover') {
            if (loading) {
                return (
                    <Spinner colour={variant !== 'text-dark' ? WHITE() : TEXT_GREY} />
                );
            } else {
                return (
                    <TextButton
                        type={type}
                        ref={ref}
                        $large={large}
                        $marginTop={marginTop}
                        $variant={variant}
                        $accentColour={accentColour}
                        {...linkProps}
                        {...props}
                    >
                        {children}
                    </TextButton>
                );
            }
        } else {
            return (
                <StyledButton
                    $variant={variant}
                    $square={square}
                    $minHeight={square ? 27 : undefined} // will be overridden if props includes a minHeight
                    $showingSpinner={loading}
                    type={type}
                    ref={ref}
                    $large={large}
                    $marginTop={marginTop}
                    // $gradients={gradients}
                    $backgroundColour={useAccentColour2 || isVideoloft ? (accentColour2 ?? accentColour) : accentColour}
                    $textColour={whiteOverAccent || (isVideoloft && accentColour2) ? WHITE() : VL_BLACK()}
                    $accentColour={accentColour}
                    $alternativeTextColour={alternativeTextColour}
                    {...linkProps}
                    {...props}
                    onClick={loading ? undefined : stopPropagation ? (e) => { e.stopPropagation(); props.onClick(e); } : props.onClick}
                >
                    {children}
                    {loading && (
                        <Spinner
                            colour={/secondary/.test(variant) ? '#000' : /danger/.test(variant) ? '#F00' : (whiteOverAccent || (isVideoloft && accentColour2) ? WHITE() : VL_BLACK())}
                            style={{ position: 'absolute' }}
                        />
                    )}
                </StyledButton>
            );
        }
    }
);

const AsyncActionButton = forwardRef(({ asyncAction, ...props }, ref) => {
    const asyncActionProps = useAsyncButtonAction(asyncAction);

    return <ButtonComponent {...asyncActionProps} {...props} ref={ref} />;
});

Button.displayName = 'Button';

export default Button;
